import { Tag } from 'components/TagsAddInput/TagsAddInput'

import { theme } from 'styles/theme'

/**
 * Converte uma string de UUIDs separados por ';' para um array de objetos Tag
 * @param tagsString - String com UUIDs separados por ';', array de strings ou array de objetos Tag
 * @param availableTags - Array de tags disponíveis para fazer o match
 * @returns Array de objetos Tag correspondentes aos UUIDs
 */
export const convertTagsStringToArray = (
  tagsString: string | string[] | Tag[] | undefined | null,
  availableTags: Tag[]
): Tag[] => {
  // Se não há tags, retorna array vazio
  if (!tagsString) {
    return []
  }

  // Se é um array vazio, retorna array vazio
  if (Array.isArray(tagsString) && tagsString.length === 0) {
    return []
  }

  // Se já é um array, verifica se são objetos Tag ou strings
  if (Array.isArray(tagsString)) {
    // Se o primeiro item é um objeto com 'id' ou 'uuid', assume que já são objetos Tag
    if (
      tagsString.length > 0 &&
      typeof tagsString[0] === 'object' &&
      tagsString[0] !== null &&
      ('id' in tagsString[0] || 'uuid' in tagsString[0])
    ) {
      // Converte uuid para id se necessário
      const convertedTags = tagsString.map((tag: any) => {
        let color = tag.color

        // Garantir que a cor tenha o formato correto
        if (color && typeof color === 'string') {
          // Se não começar com #, adicionar
          if (!color.startsWith('#')) {
            color = `#${color}`
          }
        } else {
          // Cor padrão se não houver cor
          color = theme.palette.neutral[700]
        }

        return {
          id: tag.id || tag.uuid,
          name: tag.name,
          color: color
        }
      })

      return convertedTags as Tag[]
    }

    // Se são strings e há availableTags, processa como array de IDs
    if (availableTags && availableTags.length > 0) {
      return (tagsString as string[])
        .filter((id) => id && typeof id === 'string' && id.trim())
        .map((id) =>
          availableTags.find((tag) => tag.id === (id as string).trim())
        )
        .filter((tag): tag is Tag => tag !== undefined)
    }

    return []
  }

  // Se é string, verifica se não está vazia
  if (typeof tagsString !== 'string' || !tagsString.trim()) {
    return []
  }

  // Converte string separada por ';' em array
  const tagIds = tagsString.split(';').filter((id) => id && id.trim())

  return tagIds
    .map((id) => availableTags.find((tag) => tag.id === id.trim()))
    .filter((tag): tag is Tag => tag !== undefined)
}

/**
 * Converte um array de objetos Tag para uma string de UUIDs separados por ';'
 * @param tags - Array de objetos Tag
 * @returns String com UUIDs separados por ';' (ex: "uuid1;uuid2;uuid3")
 */
export const convertTagsArrayToString = (tags: Tag[]): string => {
  if (!tags || tags.length === 0) {
    return ''
  }

  return tags.map((tag) => tag.id).join(';')
}

/**
 * Prepara os defaultValues para formulários, convertendo tags de string para array
 * @param data - Dados do formulário que podem conter tags como string ou array
 * @param availableTags - Array de tags disponíveis
 * @returns Dados com tags convertidas para array de objetos Tag
 */
export const prepareFormDataWithTags = <T extends Record<string, any>>(
  data: T,
  availableTags: Tag[]
): T & { tags?: Tag[] } => {
  // Se não há tags ou já são objetos Tag, retorna os dados como estão
  if (
    !data.tags ||
    (Array.isArray(data.tags) &&
      data.tags.length > 0 &&
      typeof data.tags[0] === 'object' &&
      'id' in data.tags[0])
  ) {
    return data
  }

  // Se há tags para converter (string ou array de strings)
  if (
    data.tags &&
    (typeof data.tags === 'string' || Array.isArray(data.tags))
  ) {
    return {
      ...data,
      tags: convertTagsStringToArray(data.tags, availableTags)
    }
  }

  return data
}
